/**
 * ツールプリセットブラウザの汎用ロジックフック。
 *
 * ハードウェアシンセの「プリセットストリップ」（保存アイコン + プリセット名 +
 * ソース切替ドロップダウン + 左右送り）を実装するための、UI 非依存の状態管理。
 * 見た目は各ツールがパネルのデザインに合わせて実装する
 * （例: `src/pages/tools/phrase-bloom/components/PresetBar.tsx`）。
 *
 * ソース = プリセットの持ち主。「MY PRESETS（自分）」と、相互フォロー
 * ユーザーのうち共有プリセットを持つ人が並ぶ。各ソースはプリセット名の
 * 一覧（`PresetSource.presets`）を持ち、ドロップダウンで 2 階層表示できる。
 * 保存は常に自分のリストへ行われ、既定では共有 ON（相互フォローへ自動公開）
 * で作成する。`shareOnSave: false` のツールは本人のみが見えるプリセットとして
 * 保存し、`showSharedSources: false` で共有ソース自体を出さない
 * （例: step-estimate）。
 *
 * ダーティ判定: 最後に読込/保存したペイロードの JSON スナップショットと
 * `getPayload()` の JSON を比較する。ペイロードは同一のオブジェクト構築
 * 順序を持つ（sanitize / build が同じキー順で生成する）前提。
 *
 * フィードバックはトーストではなく UI の状態変化で行う
 * （保存ボタンの消灯・表示名の更新など）。失敗時は状態を変えない。
 *
 * BE 仕様は soremo-soundcheck/docs/api/tool-presets.md を参照。
 */
import { useCallback, useMemo, useState } from "react";

import {
  useCreateToolPresetMutation,
  useDeleteToolPresetMutation,
  useGetMyToolPresetsQuery,
  useGetSharedToolPresetsQuery,
  useUpdateToolPresetMutation,
  type SharedToolPreset,
  type ToolPreset,
} from "../../../app/services/toolPresets";

/** 未保存状態の表示名（Serum 等に倣った INIT 表記）。defaultName 未指定時のフォールバック。 */
export const DEFAULT_PRESET_NAME = "INIT";

/** ソース識別子。"me" = 自分、number = 相互フォローユーザーの AuthUser ID。 */
export type PresetSourceId = "me" | number;

export interface PresetSummary {
  id: string;
  name: string;
}

export interface PresetSource {
  id: PresetSourceId;
  /** ドロップダウン表示名（英語表示。自分は "MY PRESETS"）。 */
  label: string;
  /** このソースのプリセット一覧（ドロップダウンの下階層表示用）。 */
  presets: PresetSummary[];
}

export interface UseToolPresetBrowserArgs {
  /** ツール識別子。BE の `VALID_TOOL_IDS` および FE ルート slug と揃える。 */
  toolId: string;
  /** 保存時に付与するペイロード形式バージョン。 */
  schemaVersion: number;
  /**
   * 未保存（プリセット未読込）状態の表示名。ツール側で現在のパラメータから
   * 自動生成した名前を渡せる（例: "140BPM-12NOTES-MAJ PENTA"）。
   * リネーム・保存・読込で明示名が付くまではこの自動名が表示され、
   * そのまま保存名にもなる。省略時は "INIT"。
   */
  defaultName?: string;
  /** 現在のツール状態をプリセットとして直列化する。 */
  getPayload(): unknown;
  /**
   * ペイロードをツールへ反映し、適用した正規化済みペイロードを返す。
   * 反映できない場合は null / undefined を返す（読込は行われない）。
   */
  applyPayload(payload: unknown, schemaVersion: number): unknown;
  /**
   * 保存時に共有 ON（相互フォローへ自動公開）にするか。false のツールでは
   * プリセットは保存した本人のみに見える。既定 true。
   */
  shareOnSave?: boolean;
  /**
   * 相互フォローユーザーの共有プリセットをソース一覧へ出すか。false なら
   * 共有一覧の取得自体を行わない。既定 true。
   */
  showSharedSources?: boolean;
}

export interface ToolPresetBrowser {
  /** ドロップダウンに並べるソース一覧（先頭は常に自分）。 */
  sources: PresetSource[];
  sourceId: PresetSourceId;
  /** 現在読み込んでいるプリセットの ID（未保存状態は null）。 */
  currentPresetId: string | null;
  /** ソースを切り替え、そのソースの先頭プリセットを読み込む。 */
  selectSource(id: PresetSourceId): void;
  /** 指定ソースの指定プリセットを読み込む（ドロップダウン下階層から）。 */
  selectPreset(sourceId: PresetSourceId, presetId: string): void;

  /** 表示中のプリセット名。 */
  name: string;
  /** リネームをコミットする（自分の保存済みプリセットならサーバにも反映）。 */
  rename(newName: string): void;

  /** 保存状態から変更があるか（未保存状態・共有プリセット閲覧中も true）。 */
  isSavable: boolean;
  /** 現在のプリセットが自分の保存済みプリセットか（= 上書き可能か）。 */
  ownLoaded: boolean;
  /** 現在の状態を新しいプリセットとして自分のリストへ保存する。 */
  saveAsNew(): void;
  /** 現在読み込んでいる自分のプリセットへ上書き保存する。 */
  overwrite(): void;
  /** 現在読み込んでいる自分のプリセットを削除する。 */
  deleteCurrent(): void;

  /** 現在のソース内で前/次のプリセットへ移動する（末尾でループ）。 */
  goPrev(): void;
  goNext(): void;
  /** 現在のソースにプリセットが 1 件以上あるか。 */
  hasPresets: boolean;

  isSaving: boolean;
  isDeleting: boolean;
}

/** 既存名と衝突しない名前を返す（"NAME" → "NAME 2" → "NAME 3" ...）。 */
export const uniquePresetName = (
  base: string,
  existingNames: readonly string[],
): string => {
  const names = new Set(existingNames);
  if (!names.has(base)) return base;
  for (let i = 2; i < 1000; i += 1) {
    const candidate = `${base} ${i}`;
    if (!names.has(candidate)) return candidate;
  }
  return `${base} ${Date.now()}`;
};

interface SharedOwnerGroup {
  label: string;
  presets: SharedToolPreset[];
}

export const useToolPresetBrowser = ({
  toolId,
  schemaVersion,
  defaultName,
  getPayload,
  applyPayload,
  shareOnSave = true,
  showSharedSources = true,
}: UseToolPresetBrowserArgs): ToolPresetBrowser => {
  const [sourceId, setSourceId] = useState<PresetSourceId>("me");
  /** 現在読み込んでいるプリセットの ID（ソース内の位置決めにも使う）。 */
  const [currentPresetId, setCurrentPresetId] = useState<string | null>(null);
  /** リネーム・読込で明示的に付いた名前。null の間は自動名（defaultName）を表示する。 */
  const [customName, setCustomName] = useState<string | null>(null);
  /** 最後に読込/保存したペイロードの JSON。null = 未保存（INIT）状態。 */
  const [snapshot, setSnapshot] = useState<string | null>(null);

  const name = customName ?? defaultName ?? DEFAULT_PRESET_NAME;

  const { data: myData } = useGetMyToolPresetsQuery(toolId);
  const { data: sharedData } = useGetSharedToolPresetsQuery(
    { toolId, limit: 100 },
    { skip: !showSharedSources },
  );

  const [createPreset, { isLoading: isCreating }] =
    useCreateToolPresetMutation();
  const [updatePreset, { isLoading: isUpdating }] =
    useUpdateToolPresetMutation();
  const [deletePreset, { isLoading: isDeleting }] =
    useDeleteToolPresetMutation();

  const myPresets = useMemo(
    () => myData?.data?.presets ?? [],
    [myData],
  );

  /** 共有プリセットを所有者ごとにまとめる（英語表示名の昇順）。 */
  const sharedByOwner = useMemo(() => {
    const groups = new Map<number, SharedOwnerGroup>();
    for (const preset of sharedData?.data?.presets ?? []) {
      const ownerId = preset.owner.id;
      const label = (
        preset.owner.display_name_en ||
        preset.owner.display_name_jp ||
        `USER ${ownerId}`
      ).toUpperCase();
      const group = groups.get(ownerId) ?? { label, presets: [] };
      group.presets.push(preset);
      groups.set(ownerId, group);
    }
    return groups;
  }, [sharedData]);

  const sources = useMemo<PresetSource[]>(() => {
    const toSummary = (p: ToolPreset | SharedToolPreset): PresetSummary => ({
      id: p.id,
      name: p.name,
    });
    const owners = [...sharedByOwner.entries()]
      .map(([id, group]) => ({
        id,
        label: group.label,
        presets: group.presets.map(toSummary),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "en"));
    return [
      { id: "me" as const, label: "MY PRESETS", presets: myPresets.map(toSummary) },
      ...owners,
    ];
  }, [myPresets, sharedByOwner]);

  const listOf = useCallback(
    (id: PresetSourceId): ReadonlyArray<ToolPreset | SharedToolPreset> =>
      id === "me" ? myPresets : (sharedByOwner.get(id)?.presets ?? []),
    [myPresets, sharedByOwner],
  );

  const activeList = listOf(sourceId);

  // ---- 読込 ----

  const loadPreset = useCallback(
    (preset: ToolPreset | SharedToolPreset, source: PresetSourceId) => {
      const applied = applyPayload(preset.payload, preset.schema_version);
      if (applied == null) return;
      setSourceId(source);
      setCurrentPresetId(preset.id);
      setCustomName(preset.name);
      setSnapshot(JSON.stringify(applied));
    },
    [applyPayload],
  );

  const selectSource = useCallback(
    (id: PresetSourceId) => {
      const list = listOf(id);
      if (list.length > 0) {
        loadPreset(list[0], id);
      } else {
        // 空のソース（自分のプリセット 0 件）は未保存（自動名）状態へ。
        setSourceId(id);
        setCurrentPresetId(null);
        setCustomName(null);
        setSnapshot(null);
      }
    },
    [listOf, loadPreset],
  );

  const selectPreset = useCallback(
    (id: PresetSourceId, presetId: string) => {
      const preset = listOf(id).find((p) => p.id === presetId);
      if (preset) loadPreset(preset, id);
    },
    [listOf, loadPreset],
  );

  const step = useCallback(
    (dir: 1 | -1) => {
      if (activeList.length === 0) return;
      const index = activeList.findIndex((p) => p.id === currentPresetId);
      const next =
        index < 0
          ? 0
          : (index + dir + activeList.length) % activeList.length;
      loadPreset(activeList[next], sourceId);
    },
    [activeList, currentPresetId, loadPreset, sourceId],
  );

  const goPrev = useCallback(() => step(-1), [step]);
  const goNext = useCallback(() => step(1), [step]);

  // ---- 保存・リネーム・削除 ----

  const ownLoaded = sourceId === "me" && currentPresetId !== null;

  const isSavable =
    !ownLoaded ||
    snapshot === null ||
    JSON.stringify(getPayload()) !== snapshot;

  const saveAsNew = useCallback(async () => {
    const payload = getPayload();
    const base = name.trim() || DEFAULT_PRESET_NAME;
    const uniqueName = uniquePresetName(
      base,
      myPresets.map((p) => p.name),
    );
    try {
      const res = await createPreset({
        toolId,
        name: uniqueName,
        payload,
        schemaVersion,
        isShared: shareOnSave, // 共有ツールでは ON（相互フォローへ自動公開）
      }).unwrap();
      const saved = res.data?.preset;
      setSourceId("me");
      setCurrentPresetId(saved?.id ?? null);
      setCustomName(saved?.name ?? uniqueName);
      setSnapshot(JSON.stringify(payload));
    } catch {
      // 失敗時は未保存状態のまま（保存ボタンが点灯し続ける）
    }
  }, [
    getPayload,
    name,
    myPresets,
    createPreset,
    toolId,
    schemaVersion,
    shareOnSave,
  ]);

  const overwrite = useCallback(async () => {
    if (!ownLoaded || !currentPresetId) return;
    const payload = getPayload();
    try {
      await updatePreset({
        toolId,
        presetId: currentPresetId,
        payload,
        schemaVersion,
        isShared: shareOnSave, // 保存のたびにツールの共有方針へ揃える
      }).unwrap();
      setSnapshot(JSON.stringify(payload));
    } catch {
      // 失敗時はダーティ状態のまま（保存ボタンが点灯し続ける）
    }
  }, [
    ownLoaded,
    currentPresetId,
    getPayload,
    updatePreset,
    toolId,
    schemaVersion,
    shareOnSave,
  ]);

  const rename = useCallback(
    async (rawName: string) => {
      const newName = rawName.trim();
      if (!newName || newName === name) return;
      if (!ownLoaded || !currentPresetId) {
        // 未保存 / 共有プリセット閲覧中はローカルの表示名だけ変える
        // （次の保存時にこの名前で自分のリストへ保存される）。
        setCustomName(newName);
        return;
      }
      try {
        await updatePreset({
          toolId,
          presetId: currentPresetId,
          name: newName,
        }).unwrap();
        setCustomName(newName);
      } catch {
        // 失敗（名前重複など）時は元の名前の表示のまま
      }
    },
    [name, ownLoaded, currentPresetId, updatePreset, toolId],
  );

  const deleteCurrent = useCallback(async () => {
    if (!ownLoaded || !currentPresetId) return;
    try {
      await deletePreset({ toolId, presetId: currentPresetId }).unwrap();
      setCurrentPresetId(null);
      setCustomName(null);
      setSnapshot(null);
    } catch {
      // 失敗時は現在のプリセットを保持
    }
  }, [ownLoaded, currentPresetId, deletePreset, toolId]);

  return {
    sources,
    sourceId,
    currentPresetId,
    selectSource,
    selectPreset,
    name,
    rename,
    isSavable,
    ownLoaded,
    saveAsNew,
    overwrite,
    deleteCurrent,
    goPrev,
    goNext,
    hasPresets: activeList.length > 0,
    isSaving: isCreating || isUpdating,
    isDeleting,
  };
};
